// Forcing a file update to resolve compilation issues
import { useState } from 'react';
import { useQueryClient, useMutation, UseMutationResult } from '@tanstack/react-query';
import { deletePropertyRequest } from '@/api/properties';
import { IProperty } from '@/types';
import { AxiosError, AxiosResponse } from 'axios';
import { useToast } from '@/components/shared/ToastProvider';

interface UseDeletePropertyReturn {
  isDeleting: boolean;
  deleteError: string | null;
  deleteProperty: (property: IProperty) => void;
  propertyToDelete: IProperty | null;
  isConfirmationModalOpen: boolean;
  closeConfirmationModal: () => void;
  confirmDeletion: () => void;
}

// Define a more specific error type if possible from your API response
interface ApiError {
  message: string;
}

export default function useDeleteProperty(): UseDeletePropertyReturn {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [propertyToDelete, setPropertyToDelete] = useState<IProperty | null>(null);
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { mutate: deletePropertyMutation, isPending: isDeleting }: UseMutationResult<AxiosResponse<void>, AxiosError<ApiError>, number> = useMutation({
    mutationFn: (id: number) => deletePropertyRequest(id),
    onSuccess: () => {
      showSuccess('Property deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      closeConfirmationModal();
    },
    onError: (error) => {
      const message = error.response?.data?.message || 'An error occurred while deleting the property.';
      showError(message);
      setDeleteError(message);
    },
  });

  const deleteProperty = (property: IProperty) => {
    setDeleteError(null); // Reset error on new attempt
    setPropertyToDelete(property);
    setConfirmationModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setConfirmationModalOpen(false);
    setPropertyToDelete(null);
    setDeleteError(null); // Also reset error on close
  };

  const confirmDeletion = () => {
    setDeleteError(null); // Reset error before new request
    if (propertyToDelete && typeof propertyToDelete.id === 'number') {
      deletePropertyMutation(propertyToDelete.id);
    }
  };

  return {
    isDeleting,
    deleteError,
    deleteProperty,
    propertyToDelete,
    isConfirmationModalOpen,
    closeConfirmationModal,
    confirmDeletion,
  };
}
